import { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Loader2, 
  Shield, 
  Users, 
  Eye, 
  Bot, 
  Phone, 
  FileText, 
  Building2, 
  UserPlus, 
  Mail, 
  ShieldCheck, 
  ShieldX, 
  Wrench, 
  History,
  AlertCircle,
  CheckCircle2,
  Ban,
  XCircle,
  Plus,
  Send,
  List,
  UserCog,
  Workflow,
  BarChart3,
  UsersRound,
  Book,
  CreditCard,
  Puzzle
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
import { useAuth } from '@/context/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

import { addMembers } from "@/api";
import { MemberPermissions, Roles } from "@/api/users/users.types";

const ROLES = [
  {
    value: "company_admin",
    label: "Company Admin",
    description: "Full access to all features with analytics view",
    icon: Shield,
  },
  {
    value: "team_member",
    label: "Team Member",
    description: "Customizable access to features",
    icon: Users,
  }
];

const getDefaultPermissions = (role: Roles): MemberPermissions => {
  if (role === "company_admin") {
    return {
      workflows: "EDITOR",
      assistant: "EDITOR",
      multi_agent: "EDITOR",
      knowledge_base: "EDITOR",
      analytics: "VIEWER", // Analytics only displays data
      billing: "VIEWER", // Billing only displays invoices/usage
      team_management: "EDITOR",
      phone_numbers: "EDITOR",
      calls: "VIEWER", // Calls only displays call logs
      tools: "EDITOR",
      files: "EDITOR",
      messaging: "EDITOR",
      rubrics: "EDITOR", // Rubrics has full CRUD
      widgets: "EDITOR"
    };
  }
  return {
    workflows: "VIEWER",
    assistant: "VIEWER",
    multi_agent: "VIEWER",
    knowledge_base: "VIEWER",
    analytics: "VIEWER",
    billing: "RESTRICTED",
    team_management: "RESTRICTED",
    phone_numbers: "VIEWER",
    calls: "VIEWER",
    tools: "VIEWER",
    files: "VIEWER",
    messaging: "RESTRICTED",
    rubrics: "VIEWER",
    widgets: "VIEWER"
  };
};

// Icons and order match the sidebar from routes.tsx for consistency
const PERMISSION_SECTIONS = [
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'rubrics', label: 'Rubrics', icon: FileText },
  { id: 'workflows', label: 'Workflows', icon: Workflow },
  { id: 'assistant', label: 'Assistants', icon: Bot },
  { id: 'phone_numbers', label: 'Phone Numbers', icon: Phone },
  { id: 'tools', label: 'Tools', icon: Wrench },
  { id: 'files', label: 'Files', icon: FileText },
  { id: 'knowledge_base', label: 'Knowledge Base', icon: Book },
  { id: 'multi_agent', label: 'Multi Agent', icon: UsersRound },
  { id: 'messaging', label: 'Messaging', icon: Phone },
  { id: 'widgets', label: 'Widgets', icon: Puzzle },
  { id: 'calls', label: 'Call Logs', icon: History },
  { id: 'team_management', label: 'Team Management', icon: Building2 },
  { id: 'billing', label: 'Billing', icon: CreditCard }
] as const;

type Permission = "EDITOR" | "VIEWER" | "RESTRICTED";

const PERMISSION_LEVELS: Record<Permission, {
  label: string;
  description: string;
  icon: LucideIcon;
  color: string;
}> = {
  EDITOR: {
    label: "Editor",
    description: "Can edit and manage content",
    icon: Users,
    color: "text-blue-500"
  },
  VIEWER: {
    label: "Viewer",
    description: "Can only view content",
    icon: Eye,
    color: "text-gray-500"
  },
  RESTRICTED: {
    label: "Restricted",
    description: "No access to this feature",
    icon: ShieldX,
    color: "text-destructive"
  }
};

const getAvailablePermissions = (role: string, sectionId: string): Permission[] => {
  // View-only sections (no CRUD operations, only display data)
  const viewOnlySections = ["analytics", "calls", "billing"];
  
  if (role === "company_admin") {
    if (viewOnlySections.includes(sectionId)) {
      return ["VIEWER"];
    }
    return ["EDITOR"];
  }
  // View-only sections for team members
  if (viewOnlySections.includes(sectionId)) {
    return ["VIEWER", "RESTRICTED"];
  }
  return ["EDITOR", "VIEWER", "RESTRICTED"];
};

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  role: z.enum(["company_admin", "team_member"]),
  permissions: z.object({
    workflows: z.enum(["EDITOR", "VIEWER", "RESTRICTED"]),
    assistant: z.enum(["EDITOR", "VIEWER", "RESTRICTED"]),
    multi_agent: z.enum(["EDITOR", "VIEWER", "RESTRICTED"]),
    knowledge_base: z.enum(["EDITOR", "VIEWER", "RESTRICTED"]),
    analytics: z.enum(["VIEWER", "RESTRICTED"]), // View-only
    billing: z.enum(["VIEWER", "RESTRICTED"]), // View-only
    team_management: z.enum(["EDITOR", "VIEWER", "RESTRICTED"]),
    phone_numbers: z.enum(["EDITOR", "VIEWER", "RESTRICTED"]),
    calls: z.enum(["VIEWER", "RESTRICTED"]), // View-only
    tools: z.enum(["EDITOR", "VIEWER", "RESTRICTED"]),
    files: z.enum(["EDITOR", "VIEWER", "RESTRICTED"]),
    messaging: z.enum(["EDITOR", "VIEWER", "RESTRICTED"]),
    rubrics: z.enum(["EDITOR", "VIEWER", "RESTRICTED"]), // Has full CRUD
    widgets: z.enum(["EDITOR", "VIEWER", "RESTRICTED"])
  })
});

type FormValues = z.infer<typeof formSchema>;

interface Member extends FormValues {
  id: string;
}

interface AddMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  existingMembers?: { email: string }[];
}

export function AddMembersDialog({
  open,
  onOpenChange,
  onSuccess,
  existingMembers = [],
}: AddMembersDialogProps) {
  const { error, success } = useEnhancedToast();
  const [isLoading, setIsLoading] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const { user } = useAuth();

  // Create refs for toast functions
  const errorRef = useRef(error);
  const successRef = useRef(success);

  // Update refs when toast functions change
  useEffect(() => {
      errorRef.current = error;
      successRef.current = success;
  }, [error, success]);

  const getFilteredRoles = () => {
    if (user?.role === 'team_member') {
      return ROLES.filter(role => role.value === 'team_member');
    }
    return ROLES;
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      role: "team_member",
      permissions: getDefaultPermissions("team_member")
    }
  });

  const isValidPermission = (value: string): value is Permission => {
    return value in PERMISSION_LEVELS;
  };

  const onRoleChange = (role: "company_admin" | "team_member") => {
    // Set default permissions based on role
    const permissions = getDefaultPermissions(role) as FormValues["permissions"];
    form.setValue("permissions", permissions);
    form.setValue("role", role);
  };

  const isPermissionDisabled = (role: string) => {
    if (role === "company_admin") {
      return true; // All permissions are locked for company_admin
    }
    return false;
  };

  const getCompanyAdminValue = (sectionId: string) => {
    return sectionId === "analytics" ? "VIEWER" : "EDITOR";
  };

  const handleAddMember = (data: FormValues) => {
    // Check if email already exists in current team members
    const emailExists = existingMembers.some(member => 
      member.email.toLowerCase() === data.email.toLowerCase()
    );

    if (emailExists) {
      errorRef.current({
        icon: Ban,
        title: "Member Already Exists",
        description: "This email is already associated with a team member.",
        duration: 4000,
      });
      return;
    }

    // Check if email already exists in pending invitations (unless editing)
    const emailInPending = members.some(member => 
      member.id !== editingMember?.id && 
      member.email.toLowerCase() === data.email.toLowerCase()
    );

    if (emailInPending) {
      errorRef.current({
        icon: Ban,
        title: "Duplicate Email",
        description: "This email is already in the invitation list.",
        duration: 4000,
      });
      return;
    }

    // Check if at least one viewer access is granted
    const hasViewerAccess = Object.values(data.permissions).some(
      permission => permission === "VIEWER" || permission === "EDITOR"
    );

    if (!hasViewerAccess && data.role !== "company_admin") {
      errorRef.current({
        icon: AlertCircle,
        title: "Invalid Permissions",
        description: "Please grant at least one viewer access to the team member. Members need minimum viewing rights to access the platform.",
        duration: 4000,
      });
      return;
    }

    const newMember = {
      ...data,
      id: crypto.randomUUID(),
    };

    if (editingMember) {
      setMembers(members.map(m =>
        m.id === editingMember.id ? newMember : m
      ));
      setEditingMember(null);
    } else {
      setMembers([...members, newMember]);
    }
    form.reset({
      email: "",
      role: "team_member",
      permissions: getDefaultPermissions("team_member")
    });
  };

  const handleEditMember = (member: Member) => {
    setEditingMember(member);
    form.reset(member);
  };

  const handleRemoveMember = (id: string) => {
    setMembers(members.filter(m => m.id !== id));
  };

  const handleSubmit = async () => {
    if (members.length === 0) {
      form.setError("email", { message: "Add at least one member" });
      return;
    }

    setIsLoading(true);
    try {
      const transformedPermissions = members.map(member => ({
        email: member.email.toLowerCase(), // Convert email to lowercase
        role: member.role,
        permissions: member.permissions
      }));
      const response = await addMembers(transformedPermissions);

      if (!response.error) {
        successRef.current({
          icon: CheckCircle2,
          title: "Members Added",
          description: response.data?.detail,
          duration: 4000,
        });
        setMembers([]);
        onOpenChange(false);
        onSuccess?.(); // Call the success callback
      } else {
        // If there are any failed additions, show them in the toast
        const failedMembers = response.data?.results.filter(r => !r.success) ?? [];
        if (failedMembers.length > 0) {
          errorRef.current({
            icon: XCircle,
            title: "Partial Success",
            description: `Failed to add ${failedMembers.length} member${failedMembers.length > 1 ? 's' : ''}: ${failedMembers.map(m => m.email).join(', ')}`,
            duration: 10000,
          });
        } else {
          // Show error toast instead of success toast in error path
          errorRef.current({
            icon: AlertCircle,
            title: "Add Members Failed",
            description: response.error || "Could not add team members",
            duration: 4000,
          });
        }
      }
    } catch (err) {
      errorRef.current({
        icon: AlertCircle,
        title: "Add Members Failed",
        description: err instanceof Error ? err.message : "Could not add team members",
        duration: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl flex flex-col gap-0 p-0 h-[85vh]">
        <DialogHeader className="border-b bg-muted/20 px-6 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <UserPlus className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 space-y-1">
              <DialogTitle className="text-base font-semibold">
                Add Team Members
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Add team members and set their roles and permissions
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 border-y overflow-hidden">
          <div className="grid md:grid-cols-2 h-full md:divide-x">
            {/* Left Column - Member Form */}
            <div className="relative h-full overflow-auto border-b md:border-b-0">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleAddMember)} className="min-h-full">
                  <div className="p-4 md:p-6 pb-20">
                    <div className="space-y-6">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem className="space-y-2">
                          <FormLabel className="text-sm font-medium flex items-center gap-2">
                            <Mail className="h-4 w-4 text-primary" />
                            Email Address
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="member@example.com"
                              className="h-9"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="role"
                      render={({ field }) => (
                        <FormItem className="space-y-2">
                          <FormLabel className="text-sm font-medium flex items-center gap-2">
                            <Shield className="h-4 w-4 text-primary" />
                            Role
                          </FormLabel>
                          <Select
                            onValueChange={(value) => onRoleChange(value as "company_admin" | "team_member")}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="h-9">
                                <SelectValue>
                                  {field.value && (() => {
                                    const selectedRole = ROLES.find(r => r.value === field.value);
                                    if (!selectedRole) return null;
                                    const Icon = selectedRole.icon;
                                    return (
                                      <div className="flex items-center gap-2">
                                        <Icon className="h-4 w-4 flex-shrink-0" />
                                        <span className="font-medium">{selectedRole.label}</span>
                                      </div>
                                    );
                                  })()}
                                </SelectValue>
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {getFilteredRoles().map((role) => {
                                const Icon = role.icon;
                                return (
                                  <SelectItem
                                    key={role.value}
                                    value={role.value}
                                    className="py-3"
                                  >
                                    <div className="flex items-center gap-3">
                                      <Icon className="h-4 w-4 flex-shrink-0" />
                                      <div className="flex flex-col gap-1">
                                        <div className="font-medium leading-none">{role.label}</div>
                                        <div className="text-xs text-muted-foreground leading-none mt-1">{role.description}</div>
                                      </div>
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    </div>

                    <Card className="shadow-sm flex-1 flex flex-col mt-6">
                    <CardHeader className="border-b bg-muted/20 px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                          <ShieldCheck className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-sm">Permissions</CardTitle>
                          <CardDescription className="text-xs">
                            Set access level for each feature
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0 flex-1 overflow-hidden">
                      <ScrollArea className="h-full p-4">
                        <div className="space-y-3">
                        {PERMISSION_SECTIONS.map((section) => {
                          const Icon = section.icon;
                          const currentRole = form.watch("role");
                          const permissionValue = form.watch(`permissions.${section.id}`);
                          const availablePermissions = getAvailablePermissions(currentRole, section.id);
                          const isDisabled = isPermissionDisabled(currentRole);
                          const fixedValue = currentRole === "company_admin" ? getCompanyAdminValue(section.id) : undefined;

                          return (
                            <div key={section.id} className="flex items-center justify-between group">
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4 text-primary" />
                                <span className="text-sm font-medium">
                                  {section.label}
                                </span>
                              </div>
                              {currentRole === "company_admin" ? (
                                <div className="flex items-center gap-2 px-2 py-1 bg-primary/5 rounded-md border border-primary/10">
                                  <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                                  <span className="text-sm font-medium text-primary">
                                    {section.id === "analytics" ? "Viewer" : "Editor"}
                                  </span>
                                </div>
                              ) : (
                                <Select
                                  onValueChange={(value: Permission) => {
                                    form.setValue(`permissions.${section.id}`, value);
                                  }}
                                  disabled={isDisabled}
                                  value={fixedValue || permissionValue}
                                >
                                  <SelectTrigger className="h-8 w-[120px] md:w-[140px] bg-background">
                                    <SelectValue>
                                      {permissionValue && (
                                        <div className="flex items-center gap-1 pr-1">
                                          <div className={cn(
                                            "p-0.5 rounded-md",
                                            permissionValue === "EDITOR" && "bg-blue-500/10",
                                            permissionValue === "VIEWER" && "bg-gray-500/10",
                                            permissionValue === "RESTRICTED" && "bg-destructive/10"
                                          )}>
                                            {(() => {
                                              const PermissionIcon = PERMISSION_LEVELS[permissionValue].icon;
                                              return (
                                                <PermissionIcon className={cn(
                                                  "h-3.5 w-3.5",
                                                  PERMISSION_LEVELS[permissionValue].color
                                                )} />
                                              );
                                            })()}
                                          </div>
                                          <span className={cn(
                                            "text-sm font-medium truncate",
                                            PERMISSION_LEVELS[permissionValue].color
                                          )}>
                                            {PERMISSION_LEVELS[permissionValue].label}
                                          </span>
                                        </div>
                                      )}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availablePermissions.map((permission) => {
                                      const { label, description, icon: Icon, color } = PERMISSION_LEVELS[permission];
                                      return (
                                        <SelectItem
                                          key={permission}
                                          value={permission}
                                          className="flex items-center gap-2"
                                        >
                                          <div className="flex items-center gap-3">
                                            <div className={cn(
                                              "p-1 rounded-md",
                                              permission === "EDITOR" && "bg-blue-500/10",
                                              permission === "VIEWER" && "bg-gray-500/10",
                                              permission === "RESTRICTED" && "bg-destructive/10"
                                            )}>
                                              <Icon className={cn(
                                                "h-3.5 w-3.5",
                                                color
                                              )} />
                                            </div>
                                            <div className="flex flex-col">
                                              <span className="text-sm font-medium">{label}</span>
                                              <span className="text-xs text-muted-foreground">{description}</span>
                                            </div>
                                          </div>
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          );
                        })}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                  </div>

                  {/* Sticky Button at Bottom */}
                  <div className="sticky bottom-0 left-0 right-0 border-t bg-background/95 backdrop-blur-sm px-4 md:px-6 py-3 shadow-lg">
                    <Button 
                      type="submit" 
                      size="sm" 
                      className="w-full gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      {editingMember ? "Update in List" : "Add to List"}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>

            {/* Right Column - Members List */}
            <div className="flex flex-col h-full overflow-hidden">
              <div className="px-4 md:px-6 pt-4 md:pt-6 pb-4 shrink-0">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <List className="h-4 w-4 text-muted-foreground" />
                      Members to Invite
                    </h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {members.length} pending invitation{members.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 pb-4 md:pb-6">
                <div className="space-y-3">
                  {members.map((member) => {
                    const roleInfo = ROLES.find(r => r.value === member.role);
                    const Icon = roleInfo?.icon || Shield;
                    
                    return (
                      <Card key={member.id} className="shadow-sm">
                        <CardHeader className="border-b bg-muted/20 px-3 py-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="flex h-6 w-6 items-center justify-center rounded bg-primary/10 shrink-0">
                                <Mail className="h-3.5 w-3.5 text-primary" />
                              </div>
                              <span className="text-sm font-medium truncate">{member.email}</span>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => handleEditMember(member)}
                              >
                                <UserCog className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                onClick={() => handleRemoveMember(member.id)}
                              >
                                <XCircle className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="p-3 space-y-2">
                          {/* Role Badge */}
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border bg-primary/5 border-primary/20 w-fit">
                            <Icon className="h-3 w-3" />
                            {roleInfo?.label}
                          </div>
                          
                          {/* Permissions - sorted by sidebar order */}
                          <div className="flex flex-wrap gap-1">
                            {PERMISSION_SECTIONS
                              .filter(section => {
                                const value = member.permissions[section.id];
                                return value && isValidPermission(value);
                              })
                              .map((section) => {
                                const value = member.permissions[section.id];
                                if (!isValidPermission(value)) return null;
                                const SectionIcon = section.icon;
                                return (
                                  <Badge
                                    key={section.id}
                                    variant="secondary"
                                    className={cn(
                                      "gap-1 text-xs",
                                      value === "EDITOR" && "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/50 dark:text-blue-400",
                                      value === "VIEWER" && "bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-950/50 dark:text-gray-400",
                                      value === "RESTRICTED" && "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/50 dark:text-rose-400"
                                    )}
                                  >
                                    <SectionIcon className="h-3 w-3" />
                                    {value === "RESTRICTED" ? "R" : value.charAt(0)}
                                  </Badge>
                                );
                              })}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t bg-muted/20 px-6 py-3 flex flex-col-reverse sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)} 
            disabled={isLoading}
            size="sm"
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={members.length === 0 || isLoading}
            size="sm"
            className="w-full sm:w-auto gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending Invitations...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Send {members.length} Invitation{members.length !== 1 ? "s" : ""}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddMembersDialog;
